import { useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { dashboardApi } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import { API_BASE } from "../../api/http";
import "../../styles/elettrobar.css";

/** Parse CSV per anteprima tabella (client-side) */
function parseCsvPreview(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') inQ = !inQ;
      else if (line[i] === "," && !inQ) {
        out.push(cur.trim());
        cur = "";
      } else cur += line[i];
    }
    out.push(cur.trim());
    return out;
  };
  const headers = parseLine(lines[0] ?? "");
  const rows = lines.slice(1).map((l) => parseLine(l)); // tutte le righe
  return { headers, rows };
}

/** Cerca colonna nell'oggetto per alias (case-insensitive, senza spazi) */
function findCol(row: Record<string, unknown>, ...aliases: string[]): string | undefined {
  const keys = Object.keys(row);
  const norm = (s: string) => s.toLowerCase().replace(/\s/g, "");
  for (const a of aliases) {
    const k = keys.find((x) => norm(x) === norm(a));
    if (k && row[k] != null && String(row[k]).trim() !== "") return String(row[k]).trim();
  }
  return undefined;
}

/** Rileva se una colonna è data/timestamp (da nome o valori) */
function looksLikeDate(colName: string, sample: string): boolean {
  const n = colName.toLowerCase();
  if (/\b(date|timestamp|data|ora|time)\b/.test(n)) return true;
  if (/^\d{4}-\d{2}-\d{2}/.test(sample) || /^\d{2}[./]\d{2}[./]\d{4}/.test(sample)) return true;
  return false;
}

/** Rileva se una colonna è numerica (da valori) */
function looksLikeNumeric(samples: string[]): boolean {
  return samples.slice(0, 10).every((s) => s === "" || !isNaN(parseFloat(s)));
}

/** Calcola dati per grafici: legge le colonne del documento e crea grafici per ciascuna voce significativa */
function computeReportCharts(rows: Record<string, unknown>[]): {
  washesPerDay: Array<{ day: string; lavaggi: number }>;
  resultPie: Array<{ name: string; value: number }>;
  programPie: Array<{ name: string; value: number }>;
  washTimeHist: Array<{ range: string; count: number }>;
  tempsByDay: Array<{ day: string; tempIniziale: number; tempFinale: number }>;
  columnPies: Array<{ title: string; data: Array<{ name: string; value: number }> }>;
  columnBars: Array<{ title: string; data: Array<{ label: string; count: number }> }>;
} {
  const washesPerDayMap = new Map<string, number>();
  const resultMap = new Map<string, number>();
  const programMap = new Map<string, number>();
  const washTimeBuckets = [0, 60, 120, 180, 240, 300, 600, 99999]; // sec: 0-1min, 1-2, 2-3, 3-4, 4-5, 5-10, 10+
  const tempsByDayMap = new Map<string, { initVals: number[]; finalVals: number[] }>();
  const washTimeCounts = new Array(washTimeBuckets.length - 1).fill(0).map((_, i) => ({
    range: `${washTimeBuckets[i]}-${washTimeBuckets[i + 1] === 99999 ? "∞" : washTimeBuckets[i + 1]}s`,
    count: 0,
  }));

  for (const row of rows) {
    const ts = findCol(row, "Timestamp", "Date", "Data", "timestamp", "date");
    const result = findCol(row, "Result", "Risultato", "result") || "—";
    const program = findCol(row, "Program", "Programma", "program") || "—";
    const washTimeStr = findCol(row, "Wash Time", "WashTime", "Cycle Time", "CycleTime", "Tempo lavaggio", "tempo di lavaggio");
    const tempInitStr = findCol(row, "Initial Wash Temp", "InitialWashTemp", "temperatura iniziale lavaggio", "Temp iniziale");
    const tempFinalStr = findCol(row, "Initial Rinse Temp", "InitialRinseTemp", "Unusual rinse temp", "temperatura finale lavaggio", "Temp finale");

    if (ts) {
      const day = ts.slice(0, 10);
      if (day.match(/^\d{4}-\d{2}-\d{2}$/) || day.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const d = day.includes(".") ? day.split(".").reverse().join("-") : day;
        washesPerDayMap.set(d, (washesPerDayMap.get(d) ?? 0) + 1);
        const tInit = tempInitStr ? parseFloat(tempInitStr) : NaN;
        const tFinal = tempFinalStr ? parseFloat(tempFinalStr) : NaN;
        if (!isNaN(tInit) || !isNaN(tFinal)) {
          const cur = tempsByDayMap.get(d) ?? { initVals: [], finalVals: [] };
          if (!isNaN(tInit)) cur.initVals.push(tInit);
          if (!isNaN(tFinal)) cur.finalVals.push(tFinal);
          tempsByDayMap.set(d, cur);
        }
      }
    }
    resultMap.set(result, (resultMap.get(result) ?? 0) + 1);
    programMap.set(program, (programMap.get(program) ?? 0) + 1);

    const washTime = washTimeStr ? parseFloat(washTimeStr) : NaN;
    if (!isNaN(washTime)) {
      for (let i = 0; i < washTimeBuckets.length - 1; i++) {
        if (washTime >= washTimeBuckets[i] && washTime < washTimeBuckets[i + 1]) {
          washTimeCounts[i].count++;
          break;
        }
      }
    }
  }

  const washesPerDay = [...washesPerDayMap.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([day, lavaggi]) => ({ day, lavaggi }));
  const tempsByDay = [...tempsByDayMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, v]) => {
      const avgInit = v.initVals.length ? v.initVals.reduce((a, b) => a + b, 0) / v.initVals.length : null;
      const avgFinal = v.finalVals.length ? v.finalVals.reduce((a, b) => a + b, 0) / v.finalVals.length : null;
      return {
        day,
        tempIniziale: avgInit != null ? Math.round(avgInit * 10) / 10 : undefined,
        tempFinale: avgFinal != null ? Math.round(avgFinal * 10) / 10 : undefined,
      };
    })
    .filter((r) => r.tempIniziale != null || r.tempFinale != null);
  const resultPie = [...resultMap.entries()].map(([name, value]) => ({ name, value }));
  const programPie = [...programMap.entries()]
    .filter(([n]) => n !== "—")
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, value]) => ({ name, value }));

  // Analisi dinamica: colonne non già coperte da washesPerDay, resultPie, programPie, washTimeHist
  const skipCols = /^(timestamp|date|data|result|risultato|program|programma|wash\s*time|cycle\s*time|tempo\s*lavaggio|rinse\s*time)/i;
  const columnPies: Array<{ title: string; data: Array<{ name: string; value: number }> }> = [];
  const columnBars: Array<{ title: string; data: Array<{ label: string; count: number }> }> = [];
  const cols = rows.length > 0 ? Object.keys(rows[0] as object) : [];
  for (const col of cols) {
    if (skipCols.test(col.replace(/\s/g, ""))) continue;
    const vals = rows.map((r) => String((r as Record<string, unknown>)[col] ?? "").trim()).filter(Boolean);
    if (vals.length === 0) continue;
    const sample = vals[0] ?? "";
    if (looksLikeDate(col, sample)) {
      const byDay = new Map<string, number>();
      vals.forEach((v) => {
        const d = v.slice(0, 10);
        const day = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : d.includes(".") ? d.split(".").reverse().join("-") : d;
        if (day) byDay.set(day, (byDay.get(day) ?? 0) + 1);
      });
      const barData = [...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, count]) => ({ label, count }));
      if (barData.length > 0) columnBars.push({ title: `${col}`, data: barData });
    } else if (looksLikeNumeric(vals)) {
      const nums = vals.map((v) => parseFloat(v)).filter((n) => !isNaN(n));
      if (nums.length === 0) continue;
      const buckets = [0, 60, 120, 180, 240, 300, 600, 999999];
      const counts = new Array(buckets.length - 1).fill(0).map((_, i) => ({
        label: `${buckets[i]}-${buckets[i + 1] === 999999 ? "∞" : buckets[i + 1]}`,
        count: 0,
      }));
      nums.forEach((n) => {
        for (let i = 0; i < buckets.length - 1; i++) {
          if (n >= buckets[i] && n < buckets[i + 1]) {
            counts[i].count++;
            break;
          }
        }
      });
      if (counts.some((c) => c.count > 0)) columnBars.push({ title: `${col}`, data: counts });
    } else {
      const byVal = new Map<string, number>();
      vals.forEach((v) => byVal.set(v, (byVal.get(v) ?? 0) + 1));
      const pieData = [...byVal.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([name, value]) => ({ name, value }));
      if (pieData.length > 0 && pieData.length <= 20) columnPies.push({ title: col, data: pieData });
    }
  }

  return { washesPerDay, resultPie, programPie, washTimeHist: washTimeCounts, tempsByDay, columnPies, columnBars };
}

const CHART_COLORS = ["#6366f1", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#06b6d4", "#84cc16"];

type CycleRow = {
  id: string;
  serialNumber: string;
  model: string;
  machineName: string;
  timestamp: string;
  cycleCounter: string;
  program: string;
  result: string;
  washTime: string | number;
  rinseTime: string | number;
  cycleTime: string | number;
  initialWashTemp: string | number;
  initialRinseTemp: string | number;
  alarm: string;
  a0: string;
};

type DashboardData = {
  washesPerDay: Array<{ day: string; lavaggi: number; frequenza?: number }>;
  washesPerHour?: Array<{ ora: number; frequenza: number }>;
  totalWashes: number;
  maintenanceAlerts: Array<{
    machineId: string;
    machineName: string;
    totalCycles: number;
    nextAt: number;
    remaining: number;
    type: string;
  }>;
  machines: Array<{ id: string; name: string; totalCycles: number }>;
  cycles?: CycleRow[];
};

export function ElettrobarDashboardWidgets() {
  const { token } = useAuth() as any;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
    created?: number;
    washesPerDay?: Array<{ day: string; frequenza: number }>;
    washesPerHour?: Array<{ ora: number; frequenza: number }>;
    cycles?: CycleRow[];
  } | null>(null);
  const createIfMissing = true;
  const [setupResult, setSetupResult] = useState<{ created: number; updated: number; serials: string[] } | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cyclesSectionRef = useRef<HTMLDivElement | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deletingMachines, setDeletingMachines] = useState(false);
  const [showAddMachine, setShowAddMachine] = useState(false);
  const [newMachineName, setNewMachineName] = useState("");
  const [newMachineSerial, setNewMachineSerial] = useState("");
  const [addingMachine, setAddingMachine] = useState(false);
  const [showGmailConfig, setShowGmailConfig] = useState(false);
  const [gmailClientId, setGmailClientId] = useState("");
  const [gmailClientSecret, setGmailClientSecret] = useState("");
  const [gmailRefreshToken, setGmailRefreshToken] = useState("");
  const [gmailEmail, setGmailEmail] = useState("lamediazione.coop@gmail.com");
  const [syncingGmail, setSyncingGmail] = useState(false);
  const [testingGmail, setTestingGmail] = useState(false);
  const [gmailTestResult, setGmailTestResult] = useState<{
    success: boolean;
    message?: string;
    count: number;
    emails: Array<{ subject: string; date: string; from: string; attachments: Array<{ filename: string; size: number }> }>;
  } | null>(null);

  /** Report lavaggi: ultimo file scaricato da Gmail (una volta al giorno) */
  const [reportLatest, setReportLatest] = useState<{
    rows: Record<string, unknown>[];
    filename: string;
    date: string;
  } | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [fetchingReport, setFetchingReport] = useState(false);

  const csvPreview = useMemo(() => parseCsvPreview(csvText), [csvText]);

  /** Dati per grafici dal report Gmail (cicli, risultato, tempo lavaggio) */
  const reportCharts = useMemo(
    () => (reportLatest?.rows?.length ? computeReportCharts(reportLatest.rows) : null),
    [reportLatest?.rows]
  );

  /** Carica l'ultimo report lavaggi (cache Gmail) */
  const loadReportLatest = () => {
    if (!token) return;
    setLoadingReport(true);
    dashboardApi
      .getElettrobarReportLatest(token)
      .then((res: any) => {
        const r = res?.data ?? res;
        if (r?.rows?.length) setReportLatest({ rows: r.rows, filename: r.filename || "", date: r.date || "" });
        else setReportLatest(null);
      })
      .catch(() => setReportLatest(null))
      .finally(() => setLoadingReport(false));
  };

  const fetchData = () => {
    setLoading(true);
    setError(null);
    dashboardApi
      .getElettrobarDashboard(token)
      .then((res: any) => {
        const payload = res?.data ?? res;
        setData(payload);
      })
      .catch((e) => setError(e?.message ?? "Errore caricamento dashboard Elettrobar"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    if (token) loadReportLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for possible future "Imposta macchine" button
  const handleSetup = () => {
    if (!csvText.trim()) return;
    dashboardApi
      .setupElettrobarMachines(token, csvText)
      .then((res: any) => {
        const r = res?.data ?? res;
        setSetupResult(r);
      })
      .catch((e) => setSetupResult({ created: 0, updated: 0, serials: [] }));
  };

  /** Carica file CSV e mostra grafici (nessuna macchina creata, solo visualizzazione) */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setImportResult(null);
    setSetupResult(null);
    setImporting(true);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      if (!text.trim()) {
        setImportResult({ imported: 0, skipped: 0, errors: ["File vuoto"] });
        setImporting(false);
        return;
      }
      setCsvText(text);
      setImporting(false);
      e.target.value = "";
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleDeleteMachines = () => {
    if (
      !window.confirm(
        "Eliminare tutte le macchine Elettrobar (cicli, contatori e record delle macchine con seriale)? Operazione irreversibile."
      )
    )
      return;
    setDeletingMachines(true);
    setImportResult(null);
    dashboardApi
      .deleteElettrobarMachines(token)
      .then((res: unknown) => {
        const r =
          (res as { data?: { deletedMachines?: number; deletedCycles?: number } })?.data ??
          (res as { deletedMachines?: number; deletedCycles?: number });
        setData(null);
        fetchData();
        alert(
          `Eliminate ${r?.deletedMachines ?? 0} macchine e ${r?.deletedCycles ?? 0} cicli.`
        );
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : "Errore cancellazione macchine";
        alert(msg);
      })
      .finally(() => setDeletingMachines(false));
  };

  const handleDeleteData = () => {
    if (!window.confirm("Cancellare tutti i cicli e contatori Elettrobar? (Le macchine restano.) Operazione irreversibile.")) return;
    setDeleting(true);
    setImportResult(null);
    dashboardApi
      .deleteElettrobarData(token)
      .then((res: unknown) => {
        const r = (res as { data?: { deletedCycles?: number; resetCounters?: number }; deletedCycles?: number; resetCounters?: number })?.data ?? (res as { deletedCycles?: number; resetCounters?: number });
        setData(null);
        fetchData();
        alert(`Eliminati ${r?.deletedCycles ?? 0} cicli, resettati ${r?.resetCounters ?? 0} contatori.`);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : "Errore cancellazione";
        alert(msg.includes("404") ? "Endpoint non raggiungibile. Verifica che il backend sia avviato sulla porta corretta." : msg);
      })
      .finally(() => setDeleting(false));
  };

  const handleClearCsvReport = () => {
    setCsvText("");
    setImportResult(null);
    setSetupResult(null);
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };


  const handleAddMachine = () => {
    if (!newMachineSerial.trim()) {
      alert("Inserire il seriale");
      return;
    }
    setAddingMachine(true);
    dashboardApi
      .createElettrobarMachine(token, newMachineName.trim(), newMachineSerial.trim())
      .then(() => {
        setNewMachineName("");
        setNewMachineSerial("");
        setShowAddMachine(false);
        fetchData();
      })
      .catch((e) => alert(e?.message ?? "Errore inserimento"))
      .finally(() => setAddingMachine(false));
  };

  const handleTestGmail = () => {
    if (!gmailClientId || !gmailClientSecret || !gmailRefreshToken || !gmailEmail) {
      alert("Compilare tutti i campi Gmail");
      return;
    }
    setTestingGmail(true);
    setGmailTestResult(null);
    dashboardApi
      .testElettrobarGmail(token, {
        clientId: gmailClientId,
        clientSecret: gmailClientSecret,
        refreshToken: gmailRefreshToken,
        email: gmailEmail,
      })
      .then((res: any) => {
        const r = res?.data ?? res;
        setGmailTestResult(r);
        if (r.success && r.count > 0) {
          alert(`Connessione Gmail OK! Trovate ${r.count} email con CSV.`);
        } else if (r.success) {
          alert("Connessione Gmail OK, ma nessuna email con CSV trovata.");
        }
      })
      .catch((e) => {
        setGmailTestResult({ success: false, message: e?.message ?? "Errore test Gmail", count: 0, emails: [] });
        alert(`Errore test Gmail: ${e?.message ?? "Errore sconosciuto"}`);
      })
      .finally(() => setTestingGmail(false));
  };

  const handleSyncGmail = () => {
    if (!gmailClientId || !gmailClientSecret || !gmailRefreshToken || !gmailEmail) {
      alert("Compilare tutti i campi Gmail");
      return;
    }
    setSyncingGmail(true);
    setImportResult(null);
    dashboardApi
      .syncElettrobarFromGmail(
        token,
        {
          clientId: gmailClientId,
          clientSecret: gmailClientSecret,
          refreshToken: gmailRefreshToken,
          email: gmailEmail,
        },
        createIfMissing
      )
      .then(async (res: any) => {
        const r = res?.data ?? res;
        if (r.success) {
          // Il CSV è già stato importato dal backend, ma vogliamo mostrarlo anche nella tabella
          // Chiamiamo di nuovo l'API per ottenere il CSV se disponibile
          // Per ora mostriamo solo il risultato dell'import
          setFileName(r.filename || "CSV da Gmail");
          setImportResult({
            imported: r.imported || 0,
            skipped: r.skipped || 0,
            errors: r.errors || [],
            created: r.created,
            washesPerDay: r.washesPerDay,
            washesPerHour: r.washesPerHour,
            cycles: r.cycles,
          });
          if (r.imported > 0) {
            fetchData();
            alert(`✅ CSV scaricato e importato con successo!\n📄 File: ${r.filename || "N/A"}\n📊 Importati: ${r.imported} cicli`);
          } else {
            alert(`⚠️ CSV scaricato da Gmail ma nessun ciclo importato.\n📄 File: ${r.filename || "N/A"}`);
          }
        } else {
          alert(`❌ Errore sincronizzazione Gmail: ${r.message || "Errore sconosciuto"}`);
        }
      })
      .catch((e) => {
        alert(`Errore sincronizzazione Gmail: ${e?.message ?? "Errore sconosciuto"}`);
      })
      .finally(() => setSyncingGmail(false));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for possible future "Importa nel DB" button
  const handleImport = () => {
    if (!csvText.trim()) return;
    setImporting(true);
    setImportResult(null);
    dashboardApi
      .importElettrobarCsv(token, csvText, createIfMissing)
      .then((res: any) => {
        const r = res?.data ?? res;
        setImportResult(r);
        if (r.imported > 0) {
          fetchData();
          // dopo l'import, scorri automaticamente alla tabella cicli
          setTimeout(() => {
            cyclesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 400);
        }
      })
      .catch((e) => setImportResult({ imported: 0, skipped: 0, errors: [e?.message ?? "Errore import"] }))
      .finally(() => setImporting(false));
  };

  if (loading) {
    return (
      <div className="elettrobar-card p-4 elettrobar-fade-in">
        <div className="text-muted d-flex align-items-center gap-2">
          <div className="spinner-border spinner-border-sm text-primary" />
          <span>Caricamento dashboard Elettrobar…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning elettrobar-fade-in" style={{ borderRadius: '12px' }}>
        <strong><i className="bi bi-exclamation-triangle me-2" />Dashboard Elettrobar</strong>: {error}
      </div>
    );
  }

  const washesPerDayRaw =
    (importResult?.washesPerDay?.length
      ? importResult.washesPerDay.map((x) => ({ day: x.day, lavaggi: x.frequenza, frequenza: x.frequenza }))
      : null) ?? (data?.washesPerDay ?? []);
  const washesPerDay = [...washesPerDayRaw].sort((a, b) => a.day.localeCompare(b.day));
  const washesPerHour =
    (importResult?.washesPerHour?.some((x) => x.frequenza > 0) ? importResult.washesPerHour : null) ??
    (data?.washesPerHour ?? []);
  const hasData = washesPerDay.some((d) => d.lavaggi > 0) || data?.machines?.length;

  return (
    <div className="elettrobar-card mb-4 elettrobar-fade-in">
      <div className="elettrobar-card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span className="d-flex align-items-center gap-2">
          <i className="bi bi-cup-straw fs-5" />
          <span>Elettrobar – Lavaggi e manutenzione</span>
        </span>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm btn-outline-light elettrobar-btn-modern"
            onClick={() => setShowAddMachine((v) => !v)}
            style={{ borderColor: 'rgba(255,255,255,0.3)' }}
          >
            <i className="bi bi-plus-lg me-1" />
            Inserisci macchina
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-light elettrobar-btn-modern"
            onClick={handleClearCsvReport}
            disabled={!csvText && !importResult && !setupResult}
            title="Svuota CSV, report e anteprima"
            style={{ borderColor: 'rgba(255,255,255,0.3)' }}
          >
            <i className="bi bi-x-circle me-1" />
            Cancella CSV / Report
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-light elettrobar-btn-modern"
            disabled={deleting}
            onClick={handleDeleteData}
            title="Cancella cicli e contatori (le macchine restano)"
            style={{ borderColor: 'rgba(255,255,255,0.3)' }}
          >
            {deleting ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="bi bi-trash me-1" />
            )}
            Cancella dati
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-light elettrobar-btn-modern"
            disabled={deletingMachines}
            onClick={handleDeleteMachines}
            title="Elimina tutte le macchine Elettrobar (cicli, contatori e macchine con seriale)"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fca5a5' }}
          >
            {deletingMachines ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="bi bi-trash3 me-1" />
            )}
            Cancella macchine
          </button>
        </div>
      </div>
      <div className="card-body">
        {/* Form inserimento macchina */}
        {showAddMachine && (
          <div className="elettrobar-add-machine-form mb-4 elettrobar-fade-in">
            <h6 className="mb-3 d-flex align-items-center gap-2">
              <i className="bi bi-plus-circle-fill text-primary" />
              <span>Inserisci macchina Elettrobar</span>
            </h6>
              <div className="row g-2 mb-2">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control form-control-sm elettrobar-input-modern"
                    placeholder="Nome (opzionale)"
                    value={newMachineName}
                    onChange={(e) => setNewMachineName(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control form-control-sm elettrobar-input-modern"
                    placeholder="Seriale *"
                    value={newMachineSerial}
                    onChange={(e) => setNewMachineSerial(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary elettrobar-btn-modern elettrobar-btn-primary me-1"
                    onClick={handleAddMachine}
                    disabled={addingMachine || !newMachineSerial.trim()}
                  >
                    {addingMachine ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-plus-lg me-1" />Aggiungi</>}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary elettrobar-btn-modern"
                    onClick={() => {
                      setShowAddMachine(false);
                      setNewMachineName("");
                      setNewMachineSerial("");
                    }}
                  >
                    Annulla
                  </button>
                </div>
              </div>
          </div>
        )}
        {/* Integrazione Gmail - Sincronizzazione automatica */}
        <div className="elettrobar-gmail-section mb-4 elettrobar-fade-in">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0 d-flex align-items-center gap-2">
              <i className="bi bi-envelope-check-fill fs-5" style={{ color: '#6366f1' }} />
              <span>Sincronizzazione automatica da Gmail</span>
            </h6>
            <button
              type="button"
              className="btn btn-sm btn-outline-info"
              onClick={() => setShowGmailConfig((v) => !v)}
            >
              {showGmailConfig ? "Nascondi" : "Mostra configurazione"}
            </button>
          </div>
          {showGmailConfig && (
            <div className="mt-3">
              <p className="small text-muted mb-3">
                Configura le credenziali Gmail per scaricare automaticamente i CSV dalle email. 
                Le email vengono cercate per mittente e allegati CSV vengono scaricati e importati automaticamente.
              </p>
              <div className="elettrobar-info-alert small mb-3">
                <strong><i className="bi bi-lightbulb-fill me-2" />Non hai Client ID e Client Secret?</strong>
                <br />
                Vai su <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-semibold">Google Cloud Console</a>, 
                crea un progetto, abilita Gmail API e crea credenziali OAuth2 (tipo "Desktop app").
                <br />
                Vedi la guida completa in: <code className="bg-white px-2 py-1 rounded">apps/backend/COME_OTTIENERE_CLIENT_ID_E_SECRET.md</code>
              </div>
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Client ID</label>
                  <input
                    type="text"
                    className="form-control form-control-sm elettrobar-input-modern"
                    placeholder="Gmail OAuth2 Client ID"
                    value={gmailClientId}
                    onChange={(e) => setGmailClientId(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Client Secret</label>
                  <input
                    type="password"
                    className="form-control form-control-sm elettrobar-input-modern"
                    placeholder="Gmail OAuth2 Client Secret"
                    value={gmailClientSecret}
                    onChange={(e) => setGmailClientSecret(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Refresh Token</label>
                  <input
                    type="password"
                    className="form-control form-control-sm elettrobar-input-modern"
                    placeholder="Gmail OAuth2 Refresh Token"
                    value={gmailRefreshToken}
                    onChange={(e) => setGmailRefreshToken(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Email da monitorare</label>
                  <input
                    type="email"
                    className="form-control form-control-sm elettrobar-input-modern"
                    placeholder="esempio@email.com"
                    value={gmailEmail}
                    onChange={(e) => setGmailEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <a
                  href={`${API_BASE}/elettrobar/gmail-oauth/authorize?clientId=${encodeURIComponent(gmailClientId)}&clientSecret=${encodeURIComponent(gmailClientSecret)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-success elettrobar-btn-modern elettrobar-btn-success"
                  style={{ display: gmailClientId && gmailClientSecret ? 'inline-block' : 'none' }}
                >
                  <i className="bi bi-key me-1" />
                  Ottieni Refresh Token (apre nuova finestra)
                </a>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-info elettrobar-btn-modern"
                  onClick={handleTestGmail}
                  disabled={testingGmail || !gmailClientId || !gmailClientSecret || !gmailRefreshToken || !gmailEmail}
                >
                  {testingGmail ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Test in corso...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-1" />
                      Test connessione
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary elettrobar-btn-modern elettrobar-btn-primary"
                  onClick={handleSyncGmail}
                  disabled={syncingGmail || !gmailClientId || !gmailClientSecret || !gmailRefreshToken || !gmailEmail}
                >
                  {syncingGmail ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Sincronizzazione...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-1" />
                      Scarica e importa CSV da Gmail
                    </>
                  )}
                </button>
              </div>
              <div className="mt-2 small text-muted">
                <strong>💡 Suggerimento:</strong> Se non hai ancora il Refresh Token, inserisci Client ID e Client Secret, 
                poi clicca "Ottieni Refresh Token" per autorizzare Gmail e ottenere il token automaticamente.
              </div>
              {gmailTestResult && (
                <div className={`mt-3 small ${gmailTestResult.success ? "elettrobar-success-alert" : "alert alert-danger"}`} style={{ borderRadius: '8px' }}>
                  {gmailTestResult.success ? (
                    <>
                      <i className="bi bi-check-circle-fill me-2" />
                      <strong>Connessione OK!</strong> Trovate {gmailTestResult.count} email con CSV.
                      {gmailTestResult.emails.length > 0 && (
                        <ul className="mt-2 mb-0">
                          {gmailTestResult.emails.map((e, i) => (
                            <li key={i}>
                              <strong>{e.subject}</strong> ({e.date}) - {e.attachments.length} allegato/i
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle-fill me-2" />
                      <strong>Errore:</strong> {gmailTestResult.message || "Errore sconosciuto"}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Report lavaggi – scaricamento automatico una volta al giorno da lamediazione.coop@gmail.com */}
        <div className="elettrobar-report-section mb-4 elettrobar-fade-in">
          <h6 className="mb-3 d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-excel-fill fs-5" style={{ color: "#0d6e3d" }} />
            <span>Report lavaggi macchina (scaricamento automatico)</span>
          </h6>
          <p className="small text-muted mb-3">
            Il report viene inviato ogni giorno all&apos;indirizzo <strong>lamediazione.coop@gmail.com</strong>.
            Configura le variabili d&apos;ambiente sul server (<code>ELETTROBAR_GMAIL_CLIENT_ID</code>, <code>ELETTROBAR_GMAIL_CLIENT_SECRET</code>, <code>ELETTROBAR_GMAIL_REFRESH_TOKEN</code>, <code>ELETTROBAR_GMAIL_EMAIL</code>) per lo scaricamento automatico alle 06:00. Oppure usa i campi Gmail sopra e clicca &quot;Scarica ora da Gmail&quot;.
          </p>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <button
              type="button"
              className="btn btn-sm btn-success elettrobar-btn-modern"
              onClick={() => {
                if (!gmailClientId || !gmailClientSecret || !gmailRefreshToken || !gmailEmail) {
                  alert("Compila i campi Gmail sopra (Mostra configurazione) e riprova.");
                  return;
                }
                setFetchingReport(true);
                dashboardApi
                  .fetchElettrobarReport(token, {
                    clientId: gmailClientId,
                    clientSecret: gmailClientSecret,
                    refreshToken: gmailRefreshToken,
                    email: gmailEmail,
                  })
                  .then((res: any) => {
                    const r = res?.data ?? res;
                    if (r?.success) {
                      loadReportLatest();
                      alert(`Report scaricato: ${r.filename} (${r.rows} righe)`);
                    } else {
                      alert(r?.message || "Nessun report trovato in Gmail");
                    }
                  })
                  .catch((e) => alert(e?.message ?? "Errore scaricamento report"))
                  .finally(() => setFetchingReport(false));
              }}
              disabled={fetchingReport || !token}
            >
              {fetchingReport ? (
                <span className="spinner-border spinner-border-sm me-1" />
              ) : (
                <i className="bi bi-cloud-download me-1" />
              )}
              Scarica ora da Gmail
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-success elettrobar-btn-modern"
              onClick={() => {
                dashboardApi.downloadElettrobarReport(token, reportLatest?.filename).catch((e) => alert(e?.message ?? "Errore download"));
              }}
              disabled={!reportLatest?.rows?.length || loadingReport || !token}
            >
              <i className="bi bi-download me-1" />
              Scarica file Excel / CSV
            </button>
            {(reportLatest?.filename || reportLatest?.date) && (
              <span className="small text-muted">
                Ultimo: {reportLatest.filename || "—"} {reportLatest.date ? `(${reportLatest.date})` : ""}
              </span>
            )}
          </div>
          {loadingReport && !reportLatest && (
            <div className="text-muted small mb-2">
              <span className="spinner-border spinner-border-sm me-1" />
              Caricamento report…
            </div>
          )}
          {reportLatest?.rows?.length && reportCharts ? (
            <div className="elettrobar-report-charts">
              <p className="small text-muted mb-3">
                Visualizzazione dati dal file <strong>{reportLatest.filename}</strong> ({reportLatest.rows.length} righe). Nessuna macchina creata.
              </p>
              <div className="row g-3 mb-4">
                {/* Diagramma 1: Data e cicli - barre verticali */}
                {reportCharts.washesPerDay.length > 0 && (
                  <div className="col-12 elettrobar-fade-in">
                    <h6 className="elettrobar-chart-title mb-2">
                      <i className="bi bi-bar-chart-fill" />
                      Data e cicli
                    </h6>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={reportCharts.washesPerDay}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8 }} />
                          <Bar dataKey="lavaggi" name="Cicli" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {/* Diagramma 2: Programmi e Risultati - barre verticali */}
                {reportCharts.programPie.length > 0 && (
                  <div className="col-12 col-lg-6 elettrobar-fade-in">
                    <h6 className="elettrobar-chart-title mb-2">
                      <i className="bi bi-bar-chart-fill" />
                      Programmi
                    </h6>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={reportCharts.programPie}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#64748b" angle={-25} textAnchor="end" interval={0} />
                          <YAxis stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8 }} />
                          <Bar dataKey="value" name="Conteggio" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {reportCharts.resultPie.length > 0 && (
                  <div className="col-12 col-lg-6 elettrobar-fade-in">
                    <h6 className="elettrobar-chart-title mb-2">
                      <i className="bi bi-bar-chart-fill" />
                      Risultati
                    </h6>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={reportCharts.resultPie}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#64748b" angle={-25} textAnchor="end" interval={0} />
                          <YAxis stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8 }} />
                          <Bar dataKey="value" name="Conteggio" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {/* Istogramma tempo di lavaggio */}
                {reportCharts.washTimeHist.some((x) => x.count > 0) && (
                  <div className="col-12 col-lg-6 elettrobar-fade-in">
                    <h6 className="elettrobar-chart-title mb-2">
                      <i className="bi bi-clock-history" />
                      Tempo di lavaggio
                    </h6>
                    <div style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={reportCharts.washTimeHist}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="range" tick={{ fontSize: 10 }} stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8 }} />
                          <Bar dataKey="count" name="Cicli" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {reportCharts.columnPies.map((col, idx) => (
                  <div key={`rpie-${idx}`} className="col-12 col-lg-6 elettrobar-fade-in">
                    <h6 className="elettrobar-chart-title mb-2"><i className="bi bi-bar-chart-fill" /> {col.title}</h6>
                    <div style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={col.data} layout="vertical" margin={{ left: 8, right: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                          <XAxis type="number" stroke="#64748b" tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8 }} />
                          <Bar dataKey="value" name="Conteggio" fill={CHART_COLORS[idx % CHART_COLORS.length]} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ))}
                {reportCharts.columnBars.map((col, idx) => (
                  <div key={`rbar-${idx}`} className="col-12 col-lg-6 elettrobar-fade-in">
                    <h6 className="elettrobar-chart-title mb-2"><i className="bi bi-bar-chart-fill" /> {col.title}</h6>
                    <div style={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={col.data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#64748b" />
                          <YAxis stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8 }} />
                          <Bar dataKey="count" name="Conteggio" fill={CHART_COLORS[idx % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ))}
              </div>
              {/* Tabella raw dati (collassabile) */}
              <details className="mt-2">
                <summary className="small text-muted" style={{ cursor: "pointer" }}>Mostra tabella dati grezzi</summary>
                <div className="table-responsive border rounded mt-2" style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table className="table table-sm elettrobar-table-modern mb-0">
                    <thead className="sticky-top bg-light">
                      <tr>
                        {(Object.keys(reportLatest.rows[0] as object) as string[]).map((h, i) => (
                          <th key={i} className="text-nowrap px-2">{String(h)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportLatest.rows.slice(0, 200).map((row, ri) => (
                        <tr key={ri}>
                          {(Object.keys(reportLatest.rows[0] as object) as string[]).map((key, ci) => (
                            <td key={ci} className="text-nowrap px-2">
                              {row[key] != null && row[key] !== undefined ? String(row[key]) : ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportLatest.rows.length > 200 && (
                    <div className="small text-muted px-2 py-1">... prime 200 righe di {reportLatest.rows.length}</div>
                  )}
                </div>
              </details>
            </div>
          ) : reportLatest?.rows?.length && !reportCharts ? (
            <div className="table-responsive border rounded" style={{ maxHeight: 400, overflowY: "auto" }}>
              <table className="table table-sm elettrobar-table-modern mb-0">
                <thead className="sticky-top bg-light">
                  <tr>
                    {(Object.keys(reportLatest.rows[0] as object) as string[]).map((h, i) => (
                      <th key={i} className="text-nowrap px-2">{String(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportLatest.rows.map((row, ri) => (
                    <tr key={ri}>
                      {(Object.keys(reportLatest.rows[0] as object) as string[]).map((key, ci) => (
                        <td key={ci} className="text-nowrap px-2">
                          {row[key] != null && row[key] !== undefined ? String(row[key]) : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !loadingReport && (
            <div className="text-muted small py-2">
              Nessun report in cache. Configura Gmail e clicca &quot;Scarica ora da Gmail&quot; oppure attendi lo scaricamento automatico (06:00).
            </div>
          )}
        </div>

        {/* Caricamento CSV - solo tabella dati, nessuna macchina creata */}
        <div className="elettrobar-csv-section mb-4 elettrobar-fade-in">
          <h6 className="mb-3 d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-spreadsheet-fill fs-5" style={{ color: '#6366f1' }} />
            <span>Carica CSV per visualizzare la tabella</span>
          </h6>
          <p className="small text-muted mb-3">
            Carica un file .csv o incolla il contenuto. Verrà mostrata la tabella con i dati esattamente come nel file. <strong>Nessuna macchina viene creata.</strong>
          </p>

          {/* Upload file */}
          <div className="mb-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="d-none"
              onChange={handleFileSelect}
            />
            <div className="elettrobar-csv-upload-area" onClick={() => fileInputRef.current?.click()}>
              <div className="elettrobar-csv-upload-icon">
                <i className="bi bi-cloud-upload" />
              </div>
              <button
                type="button"
                className="btn btn-primary elettrobar-btn-modern elettrobar-btn-primary"
                disabled={importing}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                {importing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Caricamento…
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-2" />
                    Carica file CSV
                  </>
                )}
              </button>
              <p className="small text-muted mt-2 mb-0">
                Clicca per selezionare un file .csv
              </p>
            </div>
            {fileName && !importing && (
              <span className="ms-2 small text-muted">File: {fileName}</span>
            )}
          </div>

          <p className="small text-muted mb-2">
            Oppure incolla il contenuto CSV qui sotto:
          </p>
          <div className="overflow-auto mb-3" style={{ maxHeight: 120 }}>
            <textarea
              className="form-control form-control-sm font-monospace elettrobar-input-modern"
              rows={4}
              wrap="off"
              spellCheck={false}
              style={{ whiteSpace: "pre", minWidth: "100%", resize: "none", fontSize: '0.85rem' }}
              placeholder="Header1,Header2,Header3
val1,val2,val3"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
            />
          </div>

          {/* Tabella dati CSV - stessa struttura del file */}
          {csvPreview.headers.length > 0 && (
            <div className="mt-4 elettrobar-fade-in">
              <div className="table-responsive border rounded" style={{ maxHeight: 480, overflow: "auto" }}>
                <table className="table table-sm elettrobar-table-modern mb-0" style={{ fontSize: "0.8rem", tableLayout: "auto" }}>
                  <thead className="sticky-top bg-light">
                    <tr>
                      {csvPreview.headers.map((h, i) => (
                        <th key={i} className="text-nowrap px-2">{h || `Col ${i + 1}`}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.rows.map((row, ri) => (
                      <tr key={ri}>
                        {csvPreview.headers.map((_, ci) => (
                          <td key={ci} className="text-nowrap px-2">{row[ci] !== undefined && row[ci] !== null ? String(row[ci]) : ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="small text-muted mt-1 mb-0">
                {csvPreview.rows.length} righe. Stessa struttura del CSV caricato. Nessuna macchina creata.
              </p>
            </div>
          )}
        </div>

        {/* Istogramma: giorni vs frequenza cicli */}
        <div className="elettrobar-chart-container mb-4 elettrobar-fade-in">
          <h6 className="elettrobar-chart-title">
            <i className="bi bi-bar-chart-fill" />
            <span>Istogramma – Giorni e frequenza cicli</span>
          </h6>
          <div style={{ width: "100%", minHeight: 260, height: 260 }}>
            {washesPerDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={260} minHeight={200}>
                <BarChart data={washesPerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" name="Giorni" tick={{ fontSize: 11 }} stroke="#64748b" />
                  <YAxis name="Frequenza cicli" stroke="#64748b" />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(v: number | undefined) => [v ?? 0, "Frequenza cicli"]}
                    labelFormatter={(l) => `Giorno: ${l}`}
                  />
                  <Bar dataKey="lavaggi" name="Frequenza" fill="url(#barGradient)" radius={[8, 8, 0, 0]} />
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
                <div className="text-center">
                  <i className="bi bi-graph-up-arrow fs-1 text-muted mb-2 d-block" />
                  Carica un CSV per vedere l&apos;istogramma (giorni × frequenza cicli).
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Istogramma: ore del giorno vs frequenza cicli */}
        {washesPerHour.length > 0 && washesPerHour.some((x) => x.frequenza > 0) && (
          <div className="elettrobar-chart-container mb-4 elettrobar-fade-in">
            <h6 className="elettrobar-chart-title">
              <i className="bi bi-clock-history" />
              <span>Frequenza cicli per ora del giorno</span>
            </h6>
            <div style={{ width: "100%", minHeight: 240, height: 240 }}>
              <ResponsiveContainer width="100%" height={240} minHeight={200}>
                <BarChart data={washesPerHour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="ora"
                    tickFormatter={(h) => `${h}:00`}
                    tick={{ fontSize: 10 }}
                    stroke="#64748b"
                  />
                  <YAxis name="Frequenza" stroke="#64748b" />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(v: number | undefined) => [v ?? 0, "Cicli"]}
                    labelFormatter={(h) => `Ora: ${h}:00 - ${Number(h) + 1}:00`}
                  />
                  <Bar dataKey="frequenza" name="Cicli" fill="url(#barGradient2)" radius={[8, 8, 0, 0]} />
                  <defs>
                    <linearGradient id="barGradient2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* KPI */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-4 elettrobar-fade-in">
            <div className="elettrobar-kpi-card h-100">
              <div className="elettrobar-kpi-label">Lavaggi totali</div>
              <div className="elettrobar-kpi-value">{data?.totalWashes ?? 0}</div>
              <div className="d-flex align-items-center gap-2 mt-2">
                <i className="bi bi-cup-straw text-primary" />
                <span className="small text-muted">Cicli completati</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabella cicli (CPSI) - mostrata subito dopo import CSV o da dashboard */}
        {((importResult?.cycles?.length ?? 0) > 0 || (data?.cycles?.length ?? 0) > 0) && (
          <div ref={cyclesSectionRef} className="mt-4 elettrobar-fade-in">
            <h6 className="elettrobar-chart-title mb-3">
              <i className="bi bi-table" />
              <span>Tabella cicli {importResult?.cycles ? "(importati ora)" : ""}</span>
            </h6>
            <div className="table-responsive border rounded" style={{ maxHeight: 400, overflowY: "auto" }}>
              <table className="table table-sm elettrobar-table-modern">
                <thead className="sticky-top">
                  <tr>
                    <th>Serial Number</th>
                    <th>Model</th>
                    <th>Timestamp</th>
                    <th>Cycle Counter</th>
                    <th>Program</th>
                    <th>Result</th>
                    <th>Wash Time</th>
                    <th>Rinse Time</th>
                    <th>Cycle Time</th>
                    <th>Initial Wash Temp</th>
                    <th>Initial Rinse Temp</th>
                    <th>Alarm</th>
                    <th>a0</th>
                  </tr>
                </thead>
                <tbody>
                  {(importResult?.cycles ?? data?.cycles ?? []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.serialNumber}</td>
                      <td>{row.model}</td>
                      <td>{row.timestamp ? new Date(row.timestamp).toLocaleString() : "-"}</td>
                      <td>{row.cycleCounter}</td>
                      <td>{row.program}</td>
                      <td>{row.result}</td>
                      <td>{row.washTime}</td>
                      <td>{row.rinseTime}</td>
                      <td>{row.cycleTime}</td>
                      <td>{row.initialWashTemp}</td>
                      <td>{row.initialRinseTemp}</td>
                      <td>{row.alarm}</td>
                      <td>{row.a0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!hasData && !importResult && (
          <div className="mt-3 elettrobar-info-alert small mb-0 elettrobar-fade-in">
            <i className="bi bi-info-circle-fill me-2" />
            <strong>Carica un file CSV</strong> o incolla il contenuto. Le macchine vengono create automaticamente dai seriali e i cicli importati.
          </div>
        )}
      </div>
    </div>
  );
}

